/**
 * Undo and redo, as two buttons in a table's header row.
 *
 * `Cmd+Z` already works while focus is in the grid (`TreeTable.tsx::onKeyDown`), and this is
 * the same stack behind a control somebody can *see* — asked for by the owner on 2026-08-28,
 * the day `Delete` over a selection landed. A keystroke that clears forty cells deserves a
 * way back that does not depend on knowing a shortcut, and a disabled button is also the only
 * place the screen says "there is nothing to undo".
 *
 * It reads the editing context rather than taking the stack as props, for the reason the
 * cell editor does: the stack is one object per `EditingProvider`, and a second copy of
 * `canUndo` threaded through a screen is the kind of duplicate that drifts. Without a
 * provider above it the component renders nothing — a surface that cannot edit has nothing
 * to undo, and should not draw two dead buttons saying so.
 */

import { Redo2, Undo2 } from "lucide-react";

import { Button } from "@/components/Button";
import { useMessages } from "@/lib/i18n";

import { useOptionalEditing } from "./editing";

export function UndoButtons() {
  const t = useMessages("app");
  const editing = useOptionalEditing();
  if (editing === null) return null;

  return (
    <>
      <Button
        size="sm"
        variant="tertiary"
        disabled={!editing.canUndo}
        title={t("treeTable.undo")}
        aria-label={t("treeTable.undo")}
        onClick={() => {
          void editing.undo();
        }}
      >
        <Undo2 size={14} strokeWidth={1.5} aria-hidden />
      </Button>
      <Button
        size="sm"
        variant="tertiary"
        disabled={!editing.canRedo}
        title={t("treeTable.redo")}
        aria-label={t("treeTable.redo")}
        onClick={() => {
          void editing.redo();
        }}
      >
        <Redo2 size={14} strokeWidth={1.5} aria-hidden />
      </Button>
    </>
  );
}
